<template>
  <div class="c-kdo-modal" :class="'tone-' + tone" @mousedown.self="requestClose">
    <div class="km-card" role="dialog" aria-modal="true" :aria-label="title" :style="{ maxWidth: width + 'px', }">
      <n-loader :loading="loading" />

      <div class="km-head">
        <div class="km-icon" aria-hidden="true">
          <svg
            v-if="tone === 'danger'"
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
        </div>
        <div class="km-titles">
          <h3 class="km-title">{{ title }}</h3>
          <div v-if="subtitle" class="km-subtitle">{{ subtitle }}</div>
        </div>
        <button v-if="closable" type="button" class="km-close" aria-label="Закрыть" :disabled="busy" @click="requestClose">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>

      <div ref="body" class="km-body">
        <div v-if="hint || $slots.hint" class="km-hint">
          <slot name="hint">{{ hint }}</slot>
        </div>
        <slot />
        <div v-if="error" ref="error" class="km-error" role="alert">{{ error }}</div>
      </div>

      <div v-if="!hideFooter" class="km-footer">
        <slot name="footer">
          <button type="button" class="km-btn km-cancel" :disabled="busy" @click="requestClose">{{ cancelText }}</button>
          <button type="button" class="km-btn km-confirm" :disabled="busy || confirmDisabled" @click="$emit('confirm')">
            {{ busy ? busyText : confirmText }}
          </button>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
// Единая модалка КЭДО (подтверждения, отзыв, отмена, отправка в Енбек, выбор способа подписи).
// Состав: заголовок со значком и крестиком, подсказка, тело (slot), блок ошибки и кнопки
// «Назад» / подтверждение. Внутри тела доступны общие классы для форм: km-field, km-label, km-input,
// km-note, km-section, km-group, km-callout, km-chip, km-check, km-option.
export default {
  name: 'CKdoModal',
  props: {
    title: { type: String, required: true, },
    subtitle: { type: String, default: '', },
    // текст подсказки над содержимым; для сложной разметки есть slot hint
    hint: { type: String, default: '', },
    // primary — обычное действие, danger — отзыв/отмена/удаление
    tone: { type: String, default: 'primary', },
    width: { type: Number, default: 440, },
    confirmText: { type: String, default: 'Подтвердить', },
    busyText: { type: String, default: 'Выполняем...', },
    cancelText: { type: String, default: 'Назад', },
    confirmDisabled: { type: Boolean, default: false, },
    // идёт запрос: кнопки блокируются, окно нельзя закрыть
    busy: { type: Boolean, default: false, },
    loading: { type: Boolean, default: false, },
    error: { type: String, default: '', },
    closable: { type: Boolean, default: true, },
    hideFooter: { type: Boolean, default: false, },
  },
  watch: {
    // ошибка может появиться под прокруткой формы — подводим к ней
    error(value) {
      if (!value) return
      this.$nextTick(() => {
        const el = this.$refs.error
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth', })
      })
    },
  },
  mounted() {
    this.overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    document.body.style.overflow = this.overflow || ''
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    requestClose() {
      if (this.busy || !this.closable) return
      this.$emit('close')
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.requestClose()
    },
  },
}
</script>

<style lang="scss" scoped>
.c-kdo-modal {
  --km-accent: var(--primary);
  --km-soft: #F1F4FB;
  --km-soft-border: #DCE3F5;
  --km-hint-color: #4A4A68;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1020;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);

  &.tone-danger {
    --km-accent: #C62828;
    --km-soft: #FEF1F1;
    --km-soft-border: #F6C9C9;
    --km-hint-color: #6B2A2A;
  }

  .km-card {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 100%;
    border-radius: 16px;
    background: #FFFFFF;
    box-shadow: 0 12px 40px rgba(20, 20, 60, 0.25);
    overflow: hidden;
  }

  .km-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 20px 12px 24px;

    .km-icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: var(--km-soft);
      color: var(--km-accent);
    }

    .km-titles {
      flex: 1;
      min-width: 0;
      padding-top: 1px;
    }

    .km-title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      line-height: 1.3;
      color: var(--primary);
      word-break: break-word;
    }

    .km-subtitle {
      margin-top: 2px;
      font-size: 13px;
      color: #767676;
    }

    .km-close {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 10px;
      background: transparent;
      color: #767676;
      cursor: pointer;

      &:hover:not(:disabled) {
        background: #F1F4FB;
        color: var(--primary);
      }

      &:disabled {
        opacity: 0.4;
        cursor: default;
      }
    }
  }

  .km-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 4px 24px 8px;
    font-size: 14px;
    color: #2b2b3d;
  }

  .km-hint {
    margin-bottom: 16px;
    padding: 12px 14px;
    border-radius: 12px;
    border-left: 4px solid var(--km-accent);
    background: var(--km-soft);
    color: var(--km-hint-color);
    font-size: 13px;
    line-height: 1.5;

    p {
      margin: 0 0 6px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .km-error {
    margin: 4px 0 8px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #FDE2E2;
    color: #C62828;
    font-size: 14px;
    line-height: 1.4;
    word-break: break-word;
  }

  .km-footer {
    display: flex;
    gap: 12px;
    padding: 16px 24px 20px;
    border-top: 1px solid #EEF1FA;
    background: #FFFFFF;
  }

  .km-btn {
    flex: 1;
    height: 44px;
    padding: 0 16px;
    border-radius: 12px;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s ease-in-out, background-color 0.15s ease-in-out;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }

    &.km-cancel {
      border: 1px solid var(--primary);
      background: #FFFFFF;
      color: var(--primary);

      &:hover:not(:disabled) {
        background: #F1F4FB;
      }
    }

    &.km-confirm {
      flex: 1.4;
      border: 1px solid var(--km-accent);
      background: var(--km-accent);
      color: #FFFFFF;

      &:hover:not(:disabled) {
        opacity: 0.9;
      }
    }
  }

  // ---- общие элементы форм внутри модалки (в том числе во вложенных компонентах) ----
  :deep() {
    .km-field {
      margin-bottom: 16px;

      > label, .km-label {
        display: block;
        margin-bottom: 6px;
        font-size: 13px;
        font-weight: 600;
        color: #4A4A68;
      }

      .km-optional {
        font-weight: 400;
        color: #8f8f8f;
      }
    }

    .km-note {
      margin-top: 6px;
      font-size: 12px;
      line-height: 1.4;
      color: #8f8f8f;
    }

    .km-input {
      width: 100%;
      box-sizing: border-box;
      min-height: 42px;
      padding: 9px 12px;
      border: 1px solid #DCE3F5;
      border-radius: 10px;
      background: #FFFFFF;
      color: #2b2b3d;
      font: inherit;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

      &::placeholder {
        color: #9aa0b5;
      }

      &:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(42, 39, 102, 0.12);
      }

      &:disabled {
        background: #F6F7FB;
      }
    }

    textarea.km-input {
      min-height: 0;
      resize: vertical;
      line-height: 1.4;
    }

    // n-input и n-select внутри полей тянутся на всю ширину
    .km-field .n-input, .km-field .n-select, .km-field .new-select {
      width: 100%;
    }

    .km-section {
      margin: 20px 0 10px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #8f8f8f;

      &:first-child {
        margin-top: 0;
      }
    }

    // блок полей, которые открываются галочкой
    .km-group {
      margin: 0 0 12px 26px;
      padding: 12px 14px 2px;
      border-radius: 12px;
      background: #F6F8FF;
      border: 1px solid #E7EDFF;
    }

    .km-callout {
      margin-bottom: 16px;
      padding: 12px 14px;
      border-radius: 12px;
      border-left: 4px solid #E8590C;
      background: #FFF4E8;
      color: #7A2E0B;
      font-size: 13px;
      line-height: 1.5;
    }

    .km-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 8px;
    }

    .km-chip {
      padding: 4px 10px;
      border: 1px solid #DCE3F5;
      border-radius: 999px;
      background: #FFFFFF;
      color: var(--primary);
      font-size: 12px;
      cursor: pointer;

      &:hover {
        background: #F1F4FB;
      }
    }

    // чекбокс c-checkbox: длинный текст не сжимает квадратик
    .km-check {
      margin-bottom: 12px;

      .custom-checkbox {
        align-items: flex-start;
        margin-bottom: 0;
      }

      .custom-checkbox .check {
        flex-shrink: 0;
        margin-top: 2px;
      }

      .custom-checkbox .title {
        line-height: 1.4;
      }
    }

    // крупные варианты выбора (способ подписи)
    .km-option {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      margin-bottom: 10px;
      padding: 14px 16px;
      border: 1px solid #DCE3F5;
      border-radius: 12px;
      background: #FFFFFF;
      text-align: left;
      font: inherit;
      color: var(--primary);
      cursor: pointer;
      transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;

      &:hover {
        border-color: var(--primary);
        background: #F6F8FF;
      }

      .km-option-title {
        font-weight: 700;
      }

      .km-option-text {
        margin-top: 2px;
        font-size: 12px;
        color: #767676;
      }
    }

    // длинные названия справочников переносятся, а не обрезаются
    .vs__dropdown-menu {
      max-height: 240px;
    }

    .vs__dropdown-option {
      white-space: normal;
      line-height: 1.3;
      padding: 8px 12px;
    }

    .vs__selected {
      white-space: normal;
      line-height: 1.3;
    }
  }
}

@media (max-width: 520px) {
  .c-kdo-modal {
    padding: 0;
    align-items: flex-end;

    .km-card {
      max-width: none !important;
      border-radius: 16px 16px 0 0;
    }

    .km-head {
      padding: 16px 12px 8px 16px;
    }

    .km-body {
      padding: 4px 16px 8px;
    }

    .km-footer {
      flex-direction: column-reverse;
      padding: 12px 16px 16px;
    }
  }
}
</style>
